import { formatDateTime } from "@/api/datetime";
import { Button } from "@/components/ui/button";
import { LocationMapDialog } from "@/components/location-map-dialog";
import { useProjectHours } from "@/features/projects/hooks/useProjectHours";
import { useUsers } from "@/features/users/hooks/useUsers";
import { useAllWorkers } from "@/features/workers/hooks/useWorkers";
import { formatTimeEST } from "@/lib/utils";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import { useState } from "react";
import type { Timestamp } from "firebase/firestore";

// Helper to check if location coordinates are valid (not 0,0 or very close to 0)
const isValidLocation = (location?: { lat: number; lng: number }): location is { lat: number; lng: number } => {
  if (!location) return false;
  // Filter out 0,0 coordinates and values very close to 0 (likely invalid)
  const threshold = 0.0001;
  return Math.abs(location.lat) > threshold || Math.abs(location.lng) > threshold;
};

export const Route = createFileRoute("/dashboard/customer/projects/$projectId/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId } = Route.useParams();
  const { projectHours, loading } = useProjectHours(projectId);
  const { workers } = useAllWorkers();
  const { users } = useUsers();

  // Map dialog states
  const [isMapDialogOpen, setIsMapDialogOpen] = useState(false);
  const [selectedLocationData, setSelectedLocationData] = useState<{
    clockInLocation?: { lat: number; lng: number };
    clockOutLocation?: { lat: number; lng: number };
    workerName: string;
    projectName: string;
    focusOn?: 'clockIn' | 'clockOut';
  } | null>(null);

  // Filter for approved hours only
  const approvedHours = projectHours.filter((hour) => hour.approved);

  // Function to calculate shift duration
  const calculateShiftDuration = (clockedInAt: Timestamp, clockedOutAt?: Timestamp) => {
    if (!clockedOutAt) return "Incomplete";

    const diffMs = clockedOutAt.toMillis() - clockedInAt.toMillis();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;

    return `${hours}h ${minutes}m`;
  };

  // Function to get worker name by ID
  const getWorkerName = (workerId: string) => {
    const worker = workers.find((w) => w.id === workerId);
    return worker?.name || "Unknown Worker";
  };

  // Function to get approver name by user ID
  const getApproverName = (userId?: string) => {
    if (!userId) return null;
    const approver = users.find((u) => u.uid === userId);
    return approver?.name || approver?.email || "Unknown User";
  };

  // Group approved hours by date
  const groupApprovedHoursByDate = () => {
    const grouped: { [key: string]: typeof approvedHours } = {};

    approvedHours.forEach((hour) => {
      const dateKey = hour.clockedInAt.toDate().toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(hour);
    });

    // Sort dates in descending order (most recent first)
    return Object.entries(grouped).sort(
      ([a], [b]) => new Date(b).getTime() - new Date(a).getTime(),
    );
  };

  const openLocationMap = (hour: typeof approvedHours[0], focusOn: 'clockIn' | 'clockOut' = 'clockIn') => {
    const clockInLoc = isValidLocation(hour.clockInLocation) ? hour.clockInLocation : undefined;
    const clockOutLoc = isValidLocation(hour.clockedOutLocation) ? hour.clockedOutLocation : undefined;
    const workerName = getWorkerName(hour.workerId);
    setSelectedLocationData({
      clockInLocation: clockInLoc,
      clockOutLocation: clockOutLoc,
      workerName,
      projectName: "Project", // We don't have project name in hour object here
      focusOn,
    });
    setIsMapDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-32 w-32 animate-spin rounded-full border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  const groupedApprovedHours = groupApprovedHoursByDate();
  const router = useRouter();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Button className="mb-4" onClick={() => router.history.back()}>
        Back
      </Button>
      {groupedApprovedHours.length > 0 ? (
        <div className="space-y-6">
          {groupedApprovedHours.map(([dateString, dayHours]) => (
            <div key={dateString} className="overflow-hidden rounded-lg bg-white shadow-md">
              <div className="bg-gradient-to-r from-green-500 to-blue-600 px-6 py-4">
                <h2 className="text-lg font-semibold text-white">
                  {new Date(dateString).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    timeZone: "America/New_York",
                  })}
                </h2>
                <p className="text-sm text-white/90">
                  {dayHours.length} approved {dayHours.length === 1 ? "entry" : "entries"}
                </p>
              </div>

              <div className="space-y-4 p-6">
                {dayHours.map((hour, index) => (
                  <div
                    key={hour.id || index}
                    className="rounded-lg border border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 p-4"
                  >
                    <div className="mb-3 flex items-start justify-between">
                      <div>
                        <h3 className="mb-1 font-medium text-gray-900">
                          {getWorkerName(hour.workerId)}
                        </h3>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <span className="font-medium">In:</span>{" "}
                            {formatTimeEST(hour.clockedInAt)}
                            {isValidLocation(hour.clockInLocation) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openLocationMap(hour, 'clockIn');
                                }}
                                className="cursor-pointer p-1 text-xs text-blue-600 hover:text-blue-800"
                              >
                                <MapPin className="h-3 w-3" />
                              </Button>
                            )}
                          </span>
                          {hour.clockedOutAt && (
                            <span className="flex items-center gap-1">
                              <span className="font-medium">Out:</span>{" "}
                              {formatTimeEST(hour.clockedOutAt)}
                              {isValidLocation(hour.clockedOutLocation) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openLocationMap(hour, 'clockOut');
                                  }}
                                  className="cursor-pointer p-1 text-xs text-green-600 hover:text-green-800"
                                >
                                  <MapPin className="h-3 w-3" />
                                </Button>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="mb-2 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
                          ✅ Approved
                        </div>
                        {hour.clockedOutAt && (
                          <div className="rounded-md bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-800">
                            ⏱️ {calculateShiftDuration(hour.clockedInAt, hour.clockedOutAt)}
                          </div>
                        )}
                      </div>
                    </div>

                    {hour.approvedStatusChangedAt && (
                      <div className="border-t border-gray-200 pt-2">
                        <p className="text-xs text-gray-500">
                          Approved by {getApproverName(hour.approvedStatusChangedBy) || "Unknown"} on {formatDateTime(hour.approvedStatusChangedAt)}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg bg-white p-12 text-center shadow-md">
          <div className="mb-4 text-6xl text-gray-400">📋</div>
          <h3 className="mb-2 text-xl font-medium text-gray-900">No Approved Hours Yet</h3>
          <p className="text-gray-600">
            Once work hours are completed and approved by the project manager, they will appear
            here.
          </p>
        </div>
      )}

      {/* Location Map Dialog */}
      {selectedLocationData && (
        <LocationMapDialog
          isOpen={isMapDialogOpen}
          onClose={() => setIsMapDialogOpen(false)}
          clockInLocation={selectedLocationData.clockInLocation}
          clockOutLocation={selectedLocationData.clockOutLocation}
          workerName={selectedLocationData.workerName}
          projectName={selectedLocationData.projectName}
          focusOn={selectedLocationData.focusOn}
        />
      )}
    </div>
  );
}
